import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useFirebase } from "../../Firebase/FirebaseContext";
import {
  STATUT_TRAITE,
  STATUT_A_VERIFIER,
  statutDossier,
} from "../../Utils/statutDossier";
import {
  Grid,
  TextField,
  Typography,
  Box,
  makeStyles,
  InputAdornment,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import { Search, EventNote, Schedule } from "@material-ui/icons";
import AppointmentCard from "../Elements/AppointmentCard";
import { subDays } from "date-fns";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3, 4),
    maxWidth: 1600,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  titleIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontWeight: 800,
    fontSize: 24,
    color: "#111827",
    lineHeight: 1.2,
  },
  subtitle: {
    color: "#6B7280",
    fontSize: 14,
    marginTop: 2,
  },
  statsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statChip: {
    fontWeight: 600,
    fontSize: 13,
    height: 32,
    borderRadius: 8,
  },
  searchContainer: {
    marginBottom: theme.spacing(3),
  },
  searchField: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      "& fieldset": {
        borderColor: "#E5E7EB",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "14px 16px",
    },
  },
  gridContainer: {
    marginTop: theme.spacing(1),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8, 2),
  },
  emptyIconWrapper: {
    width: 80,
    height: 80,
    borderRadius: 20,
    backgroundColor: "#F3F4F6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
  },
  emptyIcon: {
    fontSize: 40,
    color: "#9CA3AF",
  },
  emptyTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: "#111827",
    marginBottom: theme.spacing(0.5),
  },
  emptySubtitle: {
    color: "#6B7280",
    fontSize: 14,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8),
    gap: theme.spacing(2),
  },
}));

export default function LastAppointments() {
  const classes = useStyles();
  const { firebase } = useFirebase();
  const [appointments, setAppointments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const EightDaysAgo = subDays(new Date(), 8);

  useEffect(() => {
    (async () => {
      try {
        const appointmentsRefs = collection(firebase.db, "appointements");
        const q = query(
          appointmentsRefs,
          where("laboratoryId", "==", localStorage.getItem("currentLab")),
          where("ended_at", ">=", Timestamp.fromDate(EightDaysAgo)),
          orderBy("ended_at", "desc")
        );
        const qSnapshot = await getDocs(q);
        let temp = [];
        qSnapshot.forEach((appointmentDoc) => {
          temp.push({ ...appointmentDoc.data(), id: appointmentDoc.id });
        });
        setAppointments(temp);
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const filteredAppointments = appointments.filter((appointment) => {
    // La recherche doit aussi trouver un patient par son nom d'usage.
    const fullName =
      `${appointment.name} ${appointment.lastName || ""} ${appointment.maritalName || ""}`.toLowerCase();
    const sanitizedQuery = searchQuery.trim().toLowerCase();
    return fullName.includes(sanitizedQuery);
  });

  const seenCount = appointments.filter((a) => statutDossier(a) === STATUT_TRAITE).length;
  const aVerifierCount = appointments.filter((a) => statutDossier(a) === STATUT_A_VERIFIER).length;

  return (
    <div className={classes.container}>
      <Box className={classes.header}>
        <Box>
          <Box className={classes.titleRow}>
            <Box className={classes.titleIcon}>
              <EventNote style={{ color: "#fff", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography className={classes.title}>
                Derniers rendez-vous
              </Typography>
              <Typography className={classes.subtitle}>
                8 derniers jours
              </Typography>
            </Box>
          </Box>
        </Box>
        {!isLoading && appointments.length > 0 && (
          <Box className={classes.statsRow}>
            <Chip
              label={`${appointments.length} total`}
              size="small"
              className={classes.statChip}
              style={{ backgroundColor: "#EEF2FF", color: "#4338CA" }}
            />
            {seenCount > 0 && (
              <Chip
                label={`${seenCount} traité${seenCount > 1 ? "s" : ""}`}
                size="small"
                className={classes.statChip}
                style={{ backgroundColor: "#D1FAE5", color: "#065F46" }}
              />
            )}
            {aVerifierCount > 0 && (
              <Chip
                label={`${aVerifierCount} à vérifier`}
                size="small"
                className={classes.statChip}
                style={{ backgroundColor: "#f3ecff", color: "#5b21b6" }}
              />
            )}
          </Box>
        )}
      </Box>

      <Box className={classes.searchContainer}>
        <TextField
          placeholder="Rechercher un patient par nom ou prénom..."
          variant="outlined"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: "#9CA3AF" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isLoading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography style={{ color: "#6B7280", fontSize: 14 }}>
            Chargement des rendez-vous...
          </Typography>
        </Box>
      ) : filteredAppointments.length === 0 && appointments.length === 0 ? (
        <Box className={classes.emptyState}>
          <Box className={classes.emptyIconWrapper}>
            <Schedule className={classes.emptyIcon} />
          </Box>
          <Typography className={classes.emptyTitle}>
            Aucun rendez-vous
          </Typography>
          <Typography className={classes.emptySubtitle}>
            Aucun rendez-vous trouvé pour les 8 derniers jours
          </Typography>
        </Box>
      ) : filteredAppointments.length === 0 ? (
        <Box className={classes.emptyState}>
          <Box className={classes.emptyIconWrapper}>
            <Search className={classes.emptyIcon} />
          </Box>
          <Typography className={classes.emptyTitle}>
            Aucun résultat
          </Typography>
          <Typography className={classes.emptySubtitle}>
            Aucun patient ne correspond à "{searchQuery}"
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2} className={classes.gridContainer}>
          {filteredAppointments.map((appointment, i) => {
            return (
              <AppointmentCard
                appointment={appointment}
                key={`appointment-uid-${i}`}
                onMarkSeen={(id) => setAppointments((prev) => prev.map((a) => a.id === id ? { ...a, seen: true, statut: STATUT_TRAITE } : a))}
              />
            );
          })}
        </Grid>
      )}
    </div>
  );
}
